import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import MarkdownRenderer from './MarkdownRenderer';
import { Evaluation } from '../App';
import { getRoast } from '../services/roastStore';

function ReviewView() {
  const { uuid } = useParams<{ uuid: string }>();
  const [evaluation, setEvaluation] = useState<Evaluation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    // Roasts are stored locally in this browser (no shared backend), so a link
    // only resolves for the person who generated it.
    if (uuid) {
      const found = getRoast(uuid);
      if (found) {
        setEvaluation(found);
      } else {
        setError('This roast was generated in another browser, so it is not available here.');
      }
    }
    setLoading(false);
  }, [uuid]);

  const handleTweetShare = () => {
    if (!evaluation) return;

    // Extract first paragraph or use a generic message
    const firstParagraph = evaluation.full_evaluation_text.split('\n\n')[0];
    const tweetReview = firstParagraph.length > 240
      ? firstParagraph.substring(0, 237) + '...'
      : firstParagraph;

    const tweetText = encodeURIComponent(`${tweetReview}\n\nvia YourResearchSucks`);
    window.open(`https://x.com/intent/tweet?text=${tweetText}`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-dark flex items-center justify-center">
        <div className="text-white text-xl">Loading roast...</div>
      </div>
    );
  }

  if (error || !evaluation) {
    return (
      <div className="min-h-screen bg-neutral-dark flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">{error || 'Evaluation not found'}</div>
          <Link 
            to="/" 
            className="text-critique-primary hover:underline"
          >
            Go back to roast more papers
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-dark font-serif">
      <div className="max-w-6xl mx-auto p-4 md:p-8">
        <div className="bg-neutral-darker rounded-lg shadow-lg p-6 md:p-8 border border-neutral-gray">
          <div className="flex justify-between items-center mb-6">
            <Link 
              to="/"
              className="text-critique-primary hover:text-critique-secondary transition-colors"
            >
              ← Back to YourResearchSucks
            </Link>
            <button
              onClick={handleTweetShare}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-all flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
              Tweet This Roast
            </button>
          </div>
          
          {/* Paper Info */}
          <div className="mb-6">
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
              {evaluation.paper_title || 'Untitled Paper'}
            </h1>
            <p className="text-neutral-lighter mb-4">{evaluation.paper_authors || 'Unknown Authors'}</p>
            {evaluation.paper_abstract && (
              <div className="bg-neutral-gray rounded-lg p-4 border border-neutral-light">
                <h3 className="text-sm font-semibold text-critique-primary mb-2">Abstract:</h3>
                <p className="text-sm text-neutral-lighter">{evaluation.paper_abstract}</p>
              </div>
            )}
          </div>
          
          {/* Evaluation */}
          <div className="bg-neutral-gray rounded-lg p-6 border border-neutral-light">
            <h2 className="text-xl font-bold text-critique-primary mb-4">The Roast:</h2>
            <div className="prose prose-invert max-w-none">
              <MarkdownRenderer content={evaluation.full_evaluation_text} />
            </div>
          </div>
          
          {/* Metadata */}
          <div className="mt-4 text-sm text-neutral-lighter">
            <p>Roasted on: {new Date(evaluation.created_at || '').toLocaleDateString()}</p>
            {evaluation.paper_url && (
              <p>
                Original paper:{' '}
                <a 
                  href={evaluation.paper_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-critique-primary hover:underline"
                >
                  {evaluation.paper_url}
                </a>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ReviewView;